import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api, { API_BASE, getImageUrl } from '../../utils/api';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

export default function SubmitPropertyDashboard() {
    const { token, user: authUser } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [submittingPkgId, setSubmittingPkgId] = useState<string | null>(null);
    const [selectedPkg, setSelectedPkg] = useState<any>(null);
    const [transactionId, setTransactionId] = useState('');
    const [submittingPurchase, setSubmittingPurchase] = useState(false);
    const [adminUpiId, setAdminUpiId] = useState('admin@upi');
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [userProfile, setUserProfile] = useState<any>(null);
    const [availablePackages, setAvailablePackages] = useState<any[]>([]);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [currentStep, setCurrentStep] = useState(1);
    const [defaultImages, setDefaultImages] = useState<any[]>([]);

    const STEPS = [
        "Property Identity",
        "Location Details",
        "Pricing & Sell Type",
        "Dimensions & Size",
        "Room Configuration",
        "Property Specs",
        "Status & Legal",
        "Advanced Amenities",
        "Media Gallery",
        "Review & Publish"
    ];

    const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 10));
    const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

    const [form, setForm] = useState({
        title: '', price: '', area: '', description: '', address: '', city: '', state: '', zip: '',
        contactName: '', contactEmail: '', contactPhone: '', buildingAge: '',
        garage: '0', rooms: '0', bedrooms: '1', bathrooms: '1', status: 'For Sale', type: 'Houses',
        subCategory: '', furnishing: '', totalFloors: '0', propertyFloor: '0', propertySize: '', propertySizeUnit: 'sq.ft',
        facing: '', sellType: '', constructionStatus: '', reraRegistered: false, reraNumber: '', balcony: '0',
        liftAvailable: false, powerBackup: false, societyName: '', gatedCommunity: false, cornerProperty: false, parkFacing: false,
        roadWidth: '', nearbyMetro: false, parkingType: '', totalParking: '0', postedByRole: ''
    });
    const [features, setFeatures] = useState<string[]>([]);
    const [images, setImages] = useState<File[]>([]);

    useEffect(() => {
        if (token) {
            loadProfile();
            fetch(`${API_BASE}/default-lead-images`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => res.json())
                .then(data => { if (Array.isArray(data)) setDefaultImages(data); })
                .catch(console.error);
        }
    }, [token]);

    const loadProfile = async () => {
        try {
            setLoadingProfile(true);
            const profile = await api.getUserProfile(token!);
            setUserProfile(profile);
            
            // Check if user needs a package and doesn't have one
            const needsPackage = ['broker', 'owner'].includes(profile.role);
            // Relaxing check by removing remainingProperties > 0 to match broker fix
            const hasActivePackage = profile.assignedPackage && 
                                   (!profile.packageExpiry || new Date(profile.packageExpiry) > new Date());

            if (needsPackage && !hasActivePackage) {
                // Fetch packages for their role
                const pkgType = profile.role === 'broker' ? 'broker' : 'owner';
                const packages = await api.getPackages(pkgType);
                setAvailablePackages(packages);
                try {
                    const settings = await api.getSystemSettings();
                    if (settings?.adminUpiId) setAdminUpiId(settings.adminUpiId);
                } catch (e) {}
            }
        } catch (error) {
            console.error("Error loading profile:", error);
            toast.error("Failed to load user profile");
        } finally {
            setLoadingProfile(false);
        }
    };

    const openCheckout = (pkg: any) => {
        setSelectedPkg(pkg);
        setTransactionId('');
    };

    const closeCheckout = () => {
        setSelectedPkg(null);
        setTransactionId('');
    };

    const handlePurchase = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token || !selectedPkg) return;
        if (!transactionId.trim()) {
            toast.error('Please enter a valid transaction reference number');
            return;
        }

        setSubmittingPurchase(true);
        try {
            await api.purchasePlan(token, {
                packageId: selectedPkg._id,
                name: selectedPkg.name,
                price: selectedPkg.price,
                duration: selectedPkg.period || 'Unlimited',
                users: '1',
                leadAccess: userProfile?.role === 'broker' ? 'Broker Package' : 'Owner Package',
                dailyUnlock: 'Unlimited',
                transactionId
            });
            toast.success('Purchase submitted successfully! Admin will review your transaction.');
            closeCheckout();
        } catch (err) {
            toast.error('Failed to submit purchase request');
        } finally {
            setSubmittingPurchase(false);
        }
    };

    const extractAmount = (priceStr: string) => {
        if (!priceStr) return '';
        const matches = priceStr.toString().match(/\d+/g);
        return matches ? matches.join('') : '';
    };

    const handlePackageInquiry = async (packageId: string) => {
        if (submittingPkgId) return;
        try {
            setSubmittingPkgId(packageId);
            const res = await api.sendPackageInquiry(token!, packageId);
            if (res.success) {
                toast.success("Inquiry sent successfully! An administrator will contact you to activate your package.");
            } else {
                toast.error(res.message || "Failed to send inquiry");
            }
        } catch (error) {
            toast.error("Server error. Please try again.");
        } finally {
            setSubmittingPkgId(null);
        }
    };

    const statusOptions = [
        { value: 'For Rent', label: 'For Rent' },
        { value: 'For Sale', label: 'For Sale' },
    ];
    const ptypes = [
        { value: 'Apartment', label: 'Apartment' },
        { value: 'Villa', label: 'Villa' },
        { value: 'House', label: 'House' },
        { value: 'Commercial', label: 'Commercial' },
        { value: 'Office', label: 'Office' },
        { value: 'Shop', label: 'Shop' },
        { value: 'Warehouse', label: 'Warehouse' },
        { value: 'Industrial', label: 'Industrial' },
        { value: 'Factory', label: 'Factory' },
        { value: 'Plot', label: 'Plot' },
        { value: 'Land', label: 'Land' },
        { value: 'Faith Healing', label: 'Faith Healing' },
    ];
    const bedroomOptions = ['1', '2', '3', '4', '5', '6'].map(v => ({ value: v, label: v }));
    const ageOptions = ['New', '1-5 Yrs', '10+ Yrs'].map(v => ({ value: v, label: v }));
    const furnishingOptions = ['Furnished', 'Semi Furnished', 'Unfurnished'].map(v => ({ value: v, label: v }));
    const subCategoryOptions = ['Flat', 'Villa', 'Independent House', 'Penthouse', 'Duplex', 'Studio', 'Plot', 'Office Space', 'Shop', 'Warehouse'].map(v => ({ value: v, label: v }));
    const facingOptions = ['North', 'South', 'East', 'West', 'N-E', 'N-W', 'S-E', 'S-W'].map(v => ({ value: v, label: v }));
    const sellTypeOptions = ['New', 'Resale'].map(v => ({ value: v, label: v }));
    const constructionOptions = ['Ready to Move', 'Under Construction'].map(v => ({ value: v, label: v }));
    const parkingOptions = ['Covered', 'Open', 'None'].map(v => ({ value: v, label: v }));
    const allFeatures = ['Air Condition', 'Bedding', 'Heating', 'Internet', 'Microwave', 'Smoking Allow', 'Terrace', 'Balcony', 'Wi-Fi', 'Beach', 'Parking'];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const isResidential = ['Apartment', 'Villa', 'House', 'Residential', 'Flat', 'Duplex', 'Penthouse', 'Studio'].includes(form.type);
    const isLand = ['Plot', 'Land'].includes(form.type);
    const isCommercial = ['Commercial', 'Office', 'Shop', 'Warehouse', 'Industrial', 'Factory'].includes(form.type);

    const handleFeature = (feat: string) => {
        setFeatures(prev => prev.includes(feat) ? prev.filter(f => f !== feat) : [...prev, feat]);
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setImages(Array.from(e.target.files));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) { setMsg({ type: 'error', text: 'Please login first.' }); return; }
        if (!form.title || !form.price) { setMsg({ type: 'error', text: 'Property Title and Price are required.' }); return; }
        setSubmitting(true);
        setMsg(null);

        let imageUrls: string[] = [];
        if (images.length > 0) {
            try {
                const uploadRes = await api.uploadImages(token, images);
                if (uploadRes.urls) {
                    imageUrls = uploadRes.urls;
                }
            } catch (err) {
                setMsg({ type: 'error', text: 'Image upload failed.' });
                setSubmitting(false);
                return;
            }
        } else {
            let category = form.type;
            if (isResidential) category = 'Residential';
            else if (isCommercial) category = 'Commercial';
            else if (isLand) category = 'Land';

            const matchDefault = defaultImages.find(di => {
                const diType = (di.propertyType || '').toLowerCase();
                const diSub = (di.subCategory || '').toLowerCase();
                const formSub = (form.subCategory || '').toLowerCase();
                return diType === category.toLowerCase() && (diSub.includes(formSub) || formSub.includes(diSub));
            });
            if (matchDefault && (matchDefault.imageUrl || matchDefault.image)) {
                imageUrls = [matchDefault.imageUrl || matchDefault.image];
            }
        }

        try {
            const data = await api.createProperty(token, {
                ...form,
                price: Number(form.price),
                bedrooms: Number(form.bedrooms),
                bathrooms: Number(form.bathrooms),
                garage: Number(form.garage),
                rooms: Number(form.rooms),
                propertySize: Number(form.propertySize || 0),
                totalFloors: Number(form.totalFloors),
                propertyFloor: Number(form.propertyFloor),
                balcony: Number(form.balcony),
                totalParking: Number(form.totalParking),
                features,
                images: imageUrls
            });
            if (data._id) {
                setMsg({ type: 'success', text: 'Property submitted successfully!' });
                const redirectPath = authUser?.role === 'owner' ? '/owner-property' : '/my-property';
                setTimeout(() => navigate(redirectPath), 1500);
            } else {
                setMsg({ type: 'error', text: data.message || 'Failed to submit property.' });
            }
        } catch {
            setMsg({ type: 'error', text: 'Server error. Please try again.' });
        }
        setSubmitting(false);
    };

    const customSelectStyles = {
        control: (base: any) => ({
            ...base,
            padding: '4px 8px',
            borderRadius: '16px',
            border: '1px solid #f1f5f9',
            backgroundColor: '#f8fafc',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#cbd5e1'
            }
        })
    };

    const needsPackage = userProfile && ['broker', 'owner'].includes(userProfile.role);
    // If admin has assigned a package, allow access regardless of expiry date
    const hasActivePackage = userProfile && !!userProfile.assignedPackage;


    if (loadingProfile) {
        return (
            <UserDashboardLayout>
                <div className="py-20 text-center">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-bold text-black uppercase tracking-widest text-xs italic">Initializing Workspace...</p>
                </div>
            </UserDashboardLayout>
        );
    }

    if (needsPackage && !hasActivePackage) {
        // ... (Package view remains same)
        return (
            <UserDashboardLayout>
                <div className="mb-10 text-center max-w-2xl mx-auto py-10 animate__animated animate__fadeIn">
                    <div className="w-24 h-24 bg-blue-50/50 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <i className="fa-solid fa-gem text-[#05144b] text-4xl"></i>
                    </div>
                    <h1 className="text-4xl font-black text-[#05144b] mb-4 uppercase italic tracking-tighter">Subscription Required</h1>
                    <p className="text-[#05144b]/60 font-black uppercase tracking-[0.2em] text-[10px]">Please select a {userProfile.role} listing package to unlock submission capabilities.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20 animate__animated animate__fadeInUp">
                    {availablePackages.map((pkg) => (
                        <div key={pkg._id} className={`bg-white rounded-[2.5rem] border-2 ${pkg.popular ? 'border-[#ffbb00]' : 'border-slate-100'} p-8 relative overflow-hidden transition-all hover:-translate-y-2 group shadow-sm flex flex-col`}>
                            {pkg.popular && <div className="absolute top-6 right-[-35px] bg-[#ffbb00] text-[#05144b] text-[8px] font-black py-1.5 px-10 rotate-45 uppercase tracking-[0.2em] shadow-md z-10">Popular</div>}
                            
                            <div className="flex items-center gap-4 mb-8">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl bg-[#05144b]/5 text-[#05144b]`}>
                                    <i className={`fa-solid ${pkg.icon || 'fa-box'}`}></i>
                                </div>
                                <div>
                                    <h4 className="font-black text-[#05144b] uppercase tracking-wider text-xs italic">{pkg.name}</h4>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{pkg.period}</p>
                                </div>
                            </div>

                            <div className="mb-8">
                                <span className="text-4xl font-black text-[#05144b] italic">₹{pkg.price}</span>
                                <p className="text-[10px] font-bold text-slate-400 mt-2 italic capitalize">{pkg.tagline || 'Essential tools for listing Success'}</p>
                            </div>

                            <div className="space-y-3 mb-10 flex-grow">
                                {pkg.categories?.[0]?.features?.slice(0, 5).map((feat: string, i: number) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <i className="fa-solid fa-circle-check text-emerald-500 text-[10px]"></i>
                                        <span className="text-[10px] font-black text-slate-600 uppercase tracking-tighter italic">{feat}</span>
                                    </div>
                                ))}
                            </div>

                            <button 
                                onClick={() => openCheckout(pkg)}
                                className={`w-full py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all transform active:scale-95 flex items-center justify-center gap-3 italic ${pkg.popular ? 'bg-[#ffbb00] text-[#05144b] shadow-[0_10px_20px_-5px_rgba(255,187,0,0.3)] hover:bg-[#ffbb00]/90' : 'bg-[#05144b] text-white hover:bg-[#05144b]/90'}`}
                            >
                                Purchase via UPI
                            </button>
                        </div>
                    ))}
                </div>
            </UserDashboardLayout>
        );
    }

    return (
        <UserDashboardLayout>
            {/* Header Section */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-2xl font-bold text-black mb-1">Add Property</h3>
                        <p className="text-black/60 text-[10px] font-black uppercase tracking-[3px]">Professional 10-step wizard for premium listing excellence.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {userProfile?.assignedPackage && (
                            <div className="bg-[#05144b] text-white px-5 py-2 rounded-2xl border-l-[4px] border-[#ffbb00] shadow-lg flex items-center gap-2">
                                <i className="fa-solid fa-crown text-[#ffbb00] text-xs"></i>
                                <span className="text-[10px] font-black uppercase tracking-wider">{userProfile.assignedPackage.name}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Stepper Tabs Header — clickable pills like dev-add-project */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 p-4">
                <div className="d-flex items-center gap-3 overflow-x-auto pb-3 tabs-scrollbar">
                    {STEPS.map((s, idx) => {
                        const isCurrent = currentStep === idx + 1;
                        return (
                            <button
                                type="button"
                                key={idx}
                                onClick={() => setCurrentStep(idx + 1)}
                                className={`d-flex align-items-center gap-2 px-4 py-3 rounded-4 transition-all whitespace-nowrap border fw-black text-[10px] uppercase tracking-wider ${
                                    isCurrent
                                        ? 'bg-[#D4A017] text-[#05144b] border-[#D4A017] shadow-sm'
                                        : 'bg-light text-[#05144b] border-gray-200 hover:bg-gray-100 hover:text-[#05144b]'
                                }`}
                            >
                                <span className={`w-5 h-5 rounded-3 d-flex align-items-center justify-center text-[10px] fw-bold ${
                                    isCurrent ? 'bg-[#05144b] text-white' : 'bg-secondary/15 text-[#05144b]'
                                }`}>
                                    {idx + 1}
                                </span>
                                <span>{s}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 overflow-hidden">
                <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-black flex items-center justify-center text-white font-bold shadow-lg shadow-black/20">{currentStep}</div>
                            <div className="flex-1 min-w-0">
                                <span className="block text-[10px] font-bold text-black/60 uppercase tracking-widest">Step {currentStep} of 10</span>
                                <h4 className="text-sm font-bold text-black italic leading-tight">{STEPS[currentStep - 1]}</h4>
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="text-base font-black text-black">{Math.round((currentStep / 10) * 100)}% Complete</span>
                        </div>
                    </div>
                    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-black transition-all duration-700" style={{ width: `${(currentStep / 10) * 100}%` }}></div></div>
                </div>
            </div>

            {msg && (
                <div className={`p-5 rounded-2xl mb-10 flex items-center gap-4 font-black text-[10px] uppercase tracking-widest animate__animated animate__shakeX border ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-triangle-exclamation'} text-lg`}></i>
                    {msg.text}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-10">
                <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8 lg:p-12 min-h-[400px] animate__animated animate__fadeIn">
                    
                    {/* STEP 1: IDENTITY */}
                    {currentStep === 1 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-id-card text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Property Identity</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-3 md:col-span-2">
                                    <label className="text-[10px] font-black text-[#05144b]/60 uppercase tracking-widest ml-1 italic font-serif">Listing Title <span className="text-rose-500">*</span></label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-[#05144b]/10 rounded-2xl outline-none transition-all font-black text-[#05144b] placeholder:text-[#05144b]/20" name="title" value={form.title} onChange={handleChange} required placeholder="e.g. Skyline Elite 3BHK Penthouse" />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Property Type</label>
                                    <Select options={ptypes} defaultValue={ptypes[0]} styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, type: opt?.value || 'Houses' }))} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Listing Status</label>
                                    <Select options={statusOptions} defaultValue={statusOptions[1]} styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, status: opt?.value || 'For Sale' }))} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Sub Category</label>
                                    <Select options={subCategoryOptions} placeholder="Select Category" styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, subCategory: opt?.value || '' }))} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: LOCATION */}
                    {currentStep === 2 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-location-dot text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Location Details</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-3 md:col-span-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Exact Address <span className="text-rose-500">*</span></label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="address" value={form.address} onChange={handleChange} placeholder="House No, Society Name, Locality" />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">City</label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="city" value={form.city} onChange={handleChange} placeholder="e.g. Ahmedabad" />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">State</label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="state" value={form.state} onChange={handleChange} placeholder="e.g. Gujarat" />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Society/Complex Name</label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="societyName" value={form.societyName} onChange={handleChange} placeholder="e.g. Adani Shantigram" />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[#05144b]/60 uppercase tracking-widest ml-1 italic font-serif">Pincode</label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-[#05144b]/10 rounded-2xl outline-none font-black text-[#05144b]" name="zip" value={form.zip} onChange={handleChange} placeholder="3800XX" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 3: PRICING */}
                    {currentStep === 3 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-indian-rupee-sign text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Pricing & Sell Type</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Asking Price (₹) <span className="text-rose-500">*</span></label>
                                    <input type="number" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b] text-3xl" name="price" value={form.price} onChange={handleChange} required placeholder="0.00" />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Sell Type</label>
                                    <Select options={sellTypeOptions} placeholder="Choose Type" styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, sellType: opt?.value || '' }))} />
                                </div>
                                <div className="space-y-3 md:col-span-2">
                                    <div className="p-6 rounded-2xl bg-amber-50 border border-amber-100">
                                        <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest flex items-center gap-2 italic"><i className="fa-solid fa-lightbulb"></i> Guru Tip</p>
                                        <p className="text-xs font-black text-amber-900 mt-2 leading-relaxed italic">Properties with market-competitive pricing and clear 'New/Resale' status receive 3x more authentic buyer queries.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 4: DIMENSIONS */}
                    {currentStep === 4 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-ruler-combined text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Dimensions & Size</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Built-up Area</label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="area" value={form.area} onChange={handleChange} placeholder="e.g. 1550" />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Unit</label>
                                    <select className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="propertySizeUnit" value={form.propertySizeUnit} onChange={(e) => setForm(p => ({ ...p, propertySizeUnit: e.target.value }))}>
                                        <option value="sq.ft">Sq.Ft</option>
                                        <option value="sq.yd">Sq.Yd</option>
                                        <option value="sq.mt">Sq.Mt</option>
                                        <option value="Acre">Acre</option>
                                        <option value="Bigha">Bigha</option>
                                    </select>
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Total Floors</label>
                                    <input type="number" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="totalFloors" value={form.totalFloors} onChange={handleChange} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Property on Floor</label>
                                    <input type="number" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="propertyFloor" value={form.propertyFloor} onChange={handleChange} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[#05144b]/60 uppercase tracking-widest ml-1 italic font-serif">Road Width (ft)</label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-[#05144b]/10 rounded-2xl outline-none font-black text-[#05144b]" name="roadWidth" value={form.roadWidth} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 4: DIMENSIONS (Continued) */}
                    {currentStep === 4 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-ruler-combined text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Dimensions & Size</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Carpet Area</label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="area" value={form.area} onChange={handleChange} placeholder="e.g. 1550" />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Unit</label>
                                    <select className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="propertySizeUnit" value={form.propertySizeUnit} onChange={(e) => setForm(p => ({ ...p, propertySizeUnit: e.target.value }))}>
                                        <option value="sq.ft">Sq.Ft</option>
                                        <option value="sq.yd">Sq.Yd</option>
                                        <option value="sq.mt">Sq.Mt</option>
                                        <option value="Acre">Acre</option>
                                        <option value="Bigha">Bigha</option>
                                    </select>
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Total Floors</label>
                                    <input type="number" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="totalFloors" value={form.totalFloors} onChange={handleChange} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Property on Floor</label>
                                    <input type="number" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="propertyFloor" value={form.propertyFloor} onChange={handleChange} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[#05144b]/60 uppercase tracking-widest ml-1 italic font-serif">Road Width (ft)</label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-[#05144b]/10 rounded-2xl outline-none font-black text-[#05144b]" name="roadWidth" value={form.roadWidth} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 5: CONFIGURATION */}
                    {currentStep === 5 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-bed text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Room Configuration</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                                {isResidential && (
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Bedrooms</label>
                                        <Select options={bedroomOptions} defaultValue={bedroomOptions[0]} styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, bedrooms: opt?.value || '1' }))} />
                                    </div>
                                )}
                                {(isResidential || isCommercial) && (
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{isCommercial ? 'Washrooms' : 'Bathrooms'}</label>
                                        <Select options={bedroomOptions} defaultValue={bedroomOptions[0]} styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, bathrooms: opt?.value || '1' }))} />
                                    </div>
                                )}
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Balcony Count</label>
                                    <input type="number" className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="balcony" value={form.balcony} onChange={handleChange} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Parking Slots</label>
                                    <input type="number" className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="totalParking" value={form.totalParking} onChange={handleChange} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Total Rooms</label>
                                    <input type="number" className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="rooms" value={form.rooms} onChange={handleChange} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Garage</label>
                                    <input type="number" className="w-full px-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="garage" value={form.garage} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 6: PROPERTY SPECS */}
                    {currentStep === 6 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-compass text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Property Specifications</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Facing Direction</label>
                                    <Select options={facingOptions} placeholder="Choose Facing" styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, facing: opt?.value || '' }))} />
                                </div>
                                {!isLand && (
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Furnishing Status</label>
                                        <Select options={furnishingOptions} placeholder="Status" styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, furnishing: opt?.value || '' }))} />
                                    </div>
                                )}
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Building Age</label>
                                    <Select options={ageOptions} placeholder="Years" styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, buildingAge: opt?.value || '' }))} />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Parking Type</label>
                                    <Select options={parkingOptions} placeholder="Select" styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, parkingType: opt?.value || '' }))} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 7: STATUS & LEGAL */}
                    {currentStep === 7 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-scale-balanced text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Status & Legal</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Construction Status</label>
                                    <Select options={constructionOptions} placeholder="Select Status" styles={customSelectStyles} onChange={(opt) => setForm(p => ({ ...p, constructionStatus: opt?.value || '' }))} />
                                </div>
                                <div className="space-y-8">
                                    <div className="flex items-center gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-100 mt-6">
                                        <input type="checkbox" className="w-5 h-5 accent-[#05144b]" checked={form.reraRegistered} onChange={(e) => setForm(p => ({ ...p, reraRegistered: e.target.checked }))} />
                                        <div>
                                            <p className="text-[10px] font-black text-[#05144b] uppercase tracking-widest leading-none">RERA Registered</p>
                                            <p className="text-[8px] font-bold text-slate-400 mt-1">Check if the property is legally cleared.</p>
                                        </div>
                                    </div>
                                    {form.reraRegistered && (
                                        <div className="space-y-3 animate__animated animate__fadeIn">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">RERA Number</label>
                                            <input type="text" className="w-full px-6 py-4 bg-white border border-[#05144b]/20 rounded-2xl outline-none font-black text-[#05144b]" name="reraNumber" value={form.reraNumber} onChange={handleChange} placeholder="PR/GJ/XXX..." />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 8: AMENITIES */}
                    {currentStep === 8 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-star text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Advanced Amenities</h3>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {[
                                    { key: 'liftAvailable', label: 'Lift System', icon: 'fa-elevator' },
                                    { key: 'powerBackup', label: 'Power Backup', icon: 'fa-bolt' },
                                    { key: 'gatedCommunity', label: 'Gated Access', icon: 'fa-gate' },
                                    { key: 'cornerProperty', label: 'Corner Plot', icon: 'fa-vector-square' },
                                    { key: 'parkFacing', label: 'Park View', icon: 'fa-tree' },
                                    { key: 'nearbyMetro', label: 'Metro Access', icon: 'fa-train' }
                                ].map((v, i) => (
                                    <label key={i} className={`flex items-center gap-3 p-5 rounded-2xl border transition-all cursor-pointer ${form[v.key as keyof typeof form] ? 'bg-[#ffbb00]/10 border-[#ffbb00] text-[#05144b]' : 'bg-slate-50 border-slate-100 text-slate-400 hover:border-[#05144b]/20'}`}>
                                        <input type="checkbox" className="hidden" checked={form[v.key as keyof typeof form] as boolean} onChange={(e) => setForm(p => ({ ...p, [v.key]: e.target.checked }))} />
                                        <i className={`fa-solid ${v.icon} text-lg`}></i>
                                        <span className="text-[9px] font-black uppercase tracking-wider">{v.label}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="mt-10">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 italic">Standard Features</p>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {allFeatures.map((feat, i) => (
                                        <div key={i} onClick={() => handleFeature(feat)} className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer ${features.includes(feat) ? 'bg-[#05144b] text-[#ffbb00] border-[#05144b]' : 'bg-white border-[#05144b]/10 text-[#05144b]/60 hover:bg-slate-50'}`}>
                                            <i className={`fa-solid ${features.includes(feat) ? 'fa-square-check' : 'fa-square'} text-xs`}></i>
                                            <span className="text-[9px] font-black uppercase tracking-widest">{feat}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* STEP 9: MEDIA GALLERY */}
                    {currentStep === 9 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-images text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Media Assets</h3>
                            </div>
                            <div className="bg-slate-50 border-2 border-dashed border-[#05144b]/10 rounded-[2.5rem] p-12 text-center hover:border-[#05144b]/30 transition-all cursor-pointer group relative overflow-hidden">
                                <input type="file" className="absolute inset-0 opacity-0 cursor-pointer z-20" multiple accept="image/*" onChange={handleImageChange} />
                                
                                {images.length === 0 && (() => {
                                    let category = form.type;
                                    if (isResidential) category = 'Residential';
                                    else if (isCommercial) category = 'Commercial';
                                    else if (isLand) category = 'Land';
                                    
                                    const matchDefault = defaultImages.find(di => {
                                        const diType = (di.propertyType || '').toLowerCase();
                                        const diSub = (di.subCategory || '').toLowerCase();
                                        const formSub = (form.subCategory || '').toLowerCase();
                                        return diType === category.toLowerCase() && (diSub.includes(formSub) || formSub.includes(diSub));
                                    });
                                    if (matchDefault && (matchDefault.imageUrl || matchDefault.image)) {
                                        return (
                                            <div className="absolute inset-0 z-0">
                                                <img src={getImageUrl(matchDefault.imageUrl || matchDefault.image)} alt="Default Preview" className="w-full h-full object-cover blur-[2px] opacity-40" />
                                                <div className="absolute inset-0 bg-white/40"></div>
                                                <div className="absolute top-4 left-4 bg-[#ffbb00] text-[#05144b] text-[10px] font-black uppercase px-4 py-1.5 rounded-full z-10 shadow-sm border border-[#05144b]/10">
                                                    Default Image Selected
                                                </div>
                                            </div>
                                        )
                                    }
                                    return null;
                                })()}

                                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm group-hover:scale-110 transition-transform relative z-10">
                                    <i className="fa-solid fa-cloud-arrow-up text-3xl text-[#05144b]"></i>
                                </div>
                                <h5 className="font-black text-[#05144b] mb-2 uppercase tracking-tight italic relative z-10">Upload High-Res Assets</h5>
                                <p className="text-slate-500 text-[10px] font-black uppercase tracking-[2px] mb-0 relative z-10">Drag and drop or click to browse (Max 10 Images)</p>
                                {images.length > 0 && (
                                    <div className="mt-8 flex flex-wrap justify-center gap-4">
                                        <div className="px-6 py-3 bg-[#05144b] text-[#ffbb00] rounded-full font-black text-[10px] uppercase tracking-widest shadow-xl shadow-blue-900/20">
                                            <i className="fa-solid fa-circle-check"></i> {images.length} Assets Selected
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="p-6 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-start gap-4">
                                <i className="fa-solid fa-circle-info text-indigo-500 mt-1"></i>
                                <p className="text-[10px] font-black text-indigo-900 leading-relaxed uppercase tracking-widest">
                                    Quality check: Use horizontal 16:9 images for better visibility. Avoid blurry or low-light shots to increase listing trust score.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* STEP 10: REVIEW & PUBLISH */}
                    {currentStep === 10 && (
                        <div className="space-y-8 animate__animated animate__fadeInRight">
                            <div className="flex items-center gap-4 mb-4">
                                <i className="fa-solid fa-paper-plane text-2xl text-[#05144b]"></i>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic tracking-tighter">Review & Publish</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-3 md:col-span-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Detailed Description</label>
                                    <textarea className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b] min-h-[150px]" name="description" value={form.description} onChange={handleChange} placeholder="Describe the lifestyle, neighborhood, and unique selling points..." />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Point of Contact <span className="text-rose-500">*</span></label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="contactName" value={form.contactName} onChange={handleChange} placeholder="Full Name" required />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Contact Phone <span className="text-rose-500">*</span></label>
                                    <input type="text" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="contactPhone" value={form.contactPhone} onChange={handleChange} placeholder="+91 XXXX-XXXXXX" required />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Contact Email</label>
                                    <input type="email" className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-black text-[#05144b]" name="contactEmail" value={form.contactEmail} onChange={handleChange} placeholder="name@domain.com" />
                                </div>
                                <div className="p-8 rounded-[2rem] bg-[#05144b] text-white flex flex-col justify-center items-center text-center">
                                    <i className="fa-solid fa-rocket text-3xl text-[#ffbb00] mb-4"></i>
                                    <h4 className="font-black italic uppercase tracking-tighter text-lg">Ready for the Sky?</h4>
                                    <p className="text-[9px] font-black text-white/50 uppercase tracking-[2px] mt-2">By publishing, you agree to our listing protocols.</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-6 mb-20">
                    <button type="button" onClick={() => {
                        if (currentStep === 1) {
                            const exitPath = authUser?.role === 'owner' ? '/owner/dashboard' : '/my-property';
                            navigate(exitPath);
                        } else {
                            prevStep();
                        }
                    }} className="px-10 py-5 rounded-2xl bg-white border border-slate-200 text-[#05144b] font-black text-[11px] uppercase tracking-[3px] flex items-center gap-3 hover:bg-[#05144b] hover:text-white transition-all shadow-sm">
                        <i className="fa-solid fa-arrow-left"></i> {currentStep === 1 ? 'Exit Wizard' : 'Previous'}
                    </button>
                    {currentStep < 10 ? (
                        <button type="button" onClick={nextStep} className="px-12 py-5 rounded-2xl bg-[#05144b] text-white font-black text-[11px] uppercase tracking-[3px] flex items-center gap-3 hover:scale-105 transition-all shadow-xl shadow-blue-900/20 active:scale-95">
                            Continue to {STEPS[currentStep]?.toUpperCase()} <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    ) : (
                        <button type="submit" disabled={submitting} className="px-16 py-5 rounded-3xl bg-[#05144b] text-[#ffbb00] border-2 border-[#ffbb00] font-black text-sm uppercase tracking-[4px] flex items-center gap-3 hover:bg-[#ffbb00] hover:text-[#05144b] transition-all shadow-xl shadow-amber-500/10 active:scale-95 disabled:opacity-50">
                            {submitting ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Processing...</> : 'Launch Listing Now'}
                        </button>
                    )}
                </div>
            </form>
        </UserDashboardLayout>
    );
}



